var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { styled } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alterResponsiveValues, classNames as cn, getAttributes, ThemeUtils } from '../../../../../utils';
import { IconButton, IconButtonRole, IconButtonSize, IconButtonStyles } from '../../../../atoms/icon-button';
import { Link, LinkUnderlineOptions } from '../../../../atoms/link';
import { RadioButtonDesignStyles, RadioButtonSizes, RadioButtonStates } from '../../../../atoms/radio-button';
import { Text, TextVariants } from '../../../../atoms/text';
import { RadioGroup, RadioGroupOrientations } from '../../../../molecules/radio-group';
import { Container, ContainerDisplays, ContainerFlexAlignItems, ContainerFlexDirections, ContainerFlexGaps, ContainerFlexWraps, ContainerRoles } from '../../../../templates/container';
import { StockStatuses } from '../stock-info';
import { ColorSelectorTypes } from './types';
var DEFAULT_ARIA_LABEL = 'Color selector';
var ICON_SIZES = Object.values(RadioButtonSizes).reduce(function (acc, size) {
    var _a;
    var value = '';
    if (size === RadioButtonSizes.Size_5) {
        value = IconButtonSize.Medium;
    }
    else {
        value =
            size <= RadioButtonSizes.Size_4
                ? IconButtonSize.Small
                : IconButtonSize.Large;
    }
    return __assign(__assign({}, acc), (_a = {}, _a[size] = value, _a));
}, {});
var sortByStock = function (data) {
    return data === null || data === void 0 ? void 0 : data.slice().sort(function (a, b) {
        if (a.stock === StockStatuses.OutOfStock) {
            return 1;
        }
        if (b.stock === StockStatuses.OutOfStock) {
            return -1;
        }
        return 0;
    });
};
var getIconSizeByColorDotSize = function (size) {
    return ICON_SIZES[size] || IconButtonSize.Medium;
};
var ColorSelectorContainer = styled(Container, {
    name: 'MuiColorSelector',
    overridesResolver: function (wrapperProps, styles) { return [styles.root]; }
})({});
var CS = function (_a) {
    var showTextProp = _a.showText, currentValueProp = _a.currentValue, _b = _a.colorVariants, colorVariantsProp = _b === void 0 ? [] : _b, _c = _a.sortByStock, sortByStockProp = _c === void 0 ? true : _c, nameProp = _a.name, idProp = _a.id, _d = _a.type, typeProp = _d === void 0 ? ColorSelectorTypes.Hyperlink : _d, _e = _a.size, sizeProp = _e === void 0 ? RadioButtonSizes.Size_5 : _e, maxColorsPerViewProp = _a.maxColorsPerView, iconProp = _a.icon, linkUrlProp = _a.linkUrl, ariaLabelProp = _a["aria-label"], onChangeProp = _a.onChange, rest = __rest(_a, ["showText", "currentValue", "colorVariants", "sortByStock", "name", "id", "type", "size", "maxColorsPerView", "icon", "linkUrl", 'aria-label', "onChange"]);
    var _f = useState(currentValueProp), color = _f[0], setColor = _f[1];
    var colorVariants = useMemo(function () {
        return sortByStockProp ? sortByStock(colorVariantsProp) : colorVariantsProp;
    }, [colorVariantsProp, sortByStockProp]);
    var trunc = useMemo(function () {
        return Math.max(1, maxColorsPerViewProp || (sizeProp <= RadioButtonSizes.Size_4 ? 7 : 5));
    }, [maxColorsPerViewProp, sizeProp]);
    var currentVariant = useMemo(function () { return (colorVariantsProp === null || colorVariantsProp === void 0 ? void 0 : colorVariantsProp.find(function (variant) { return variant.value === color; })) || {}; }, [color, colorVariantsProp]);
    var containerConfig = __assign({ display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexGap: ContainerFlexGaps.Size_2, id: idProp }, getAttributes(rest));
    var textConfig = {
        content: currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.colorLabel,
        variant: TextVariants.Body2
    };
    var linksWrapperConfig = {
        display: ContainerDisplays.Flex,
        flexDirection: ContainerFlexDirections.Row,
        flexWrap: ContainerFlexWraps.Wrap,
        flexGap: ContainerFlexGaps.Size_2
    };
    var fontSize = alterResponsiveValues(sizeProp, function (sizeValue) {
        var adaptedValue = null;
        if (sizeValue) {
            adaptedValue = ThemeUtils.getComputedSpacing(Number(sizeValue));
        }
        return adaptedValue;
    });
    var onLinkFocus = useCallback(function (e) {
        var _a, _b, _c;
        e.preventDefault();
        if ((_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.attributes['data-value']) === null || _b === void 0 ? void 0 : _b.value) {
            setColor((_c = colorVariantsProp === null || colorVariantsProp === void 0 ? void 0 : colorVariantsProp.find(function (variant) {
                var _a, _b;
                return variant.value ===
                    ((_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.attributes['data-value']) === null || _b === void 0 ? void 0 : _b.value);
            })) === null || _c === void 0 ? void 0 : _c.value);
        }
    }, [colorVariantsProp]);
    var blurColorChange = useCallback(function () {
        setColor(currentValueProp);
    }, [currentValueProp]);
    var linkColorSelector = useMemo(function () {
        return colorVariants.map(function (variant) {
            var linkConfig = __assign({ className: cn(variant.value === (currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.value) && 'selected', variant.stock === StockStatuses.OutOfStock && 'empty'), linkUrl: variant.linkUrl, 'data-value': variant.value, underline: LinkUnderlineOptions.None, onClick: function (e) {
                    e.preventDefault();
                    onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, variant);
                }, onMouseEnter: onLinkFocus, onMouseLeave: blurColorChange, onFocus: onLinkFocus, onBlur: blurColorChange }, getAttributes(variant));
            var colorDotConfig = {
                height: fontSize,
                width: fontSize,
                background: variant.colorHex,
                'data-value': variant.value
            };
            return variant.linkUrl ? (React.createElement(Link, __assign({ key: "".concat(variant.value, "-").concat(variant.colorHex) }, linkConfig),
                React.createElement(Container, __assign({}, colorDotConfig)))) : null;
        });
    }, [
        blurColorChange,
        colorVariants,
        currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.value,
        fontSize,
        onChangeProp,
        onLinkFocus
    ]);
    var radioButtons = useMemo(function () {
        return colorVariants.map(function (variant) { return (__assign({ onChange: function (e) {
                e.preventDefault();
                if (variant === null || variant === void 0 ? void 0 : variant.value) {
                    setColor(variant.value);
                }
                onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(e, variant);
            }, key: "".concat(variant.value, "-").concat(variant.colorHex), size: sizeProp, designStyle: RadioButtonDesignStyles.Rounded, color: variant.colorHex, checked: variant.value === (currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.value), value: variant.value, state: variant.stock === StockStatuses.OutOfStock
                ? RadioButtonStates.Disabled
                : RadioButtonStates.Default }, getAttributes(variant))); });
    }, [colorVariants, currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.value, onChangeProp, sizeProp]);
    var radioGroupConfig = useMemo(function () { return (__assign({ name: nameProp, id: idProp, value: currentVariant === null || currentVariant === void 0 ? void 0 : currentVariant.value, radioButtons: radioButtons.slice(0, trunc), designStyle: RadioButtonDesignStyles.Rounded, row: RadioGroupOrientations.Horizontal, wrap: false }, getAttributes(currentVariant))); }, [currentVariant, idProp, nameProp, radioButtons, trunc]);
    var iconButtonConfig = {
        linkUrl: linkUrlProp,
        iconButtonRole: IconButtonRole.Link,
        icon: iconProp || 'pmi-icon-chevronright',
        size: getIconSizeByColorDotSize(sizeProp),
        hasMarginBottom: false,
        background: 'transparent',
        style: IconButtonStyles.Basic,
        'aria-label': ariaLabelProp
    };
    useEffect(function () {
        if (typeProp !== ColorSelectorTypes.Hyperlink) {
            setColor(function (prevColor) {
                if (currentValueProp !== prevColor) {
                    return currentValueProp;
                }
                return prevColor;
            });
        }
    }, [currentValueProp, typeProp]);
    return (React.createElement(ColorSelectorContainer, null, Boolean(colorVariantsProp === null || colorVariantsProp === void 0 ? void 0 : colorVariantsProp.length) &&
        (typeProp === ColorSelectorTypes.Hyperlink ? (React.createElement(Container, __assign({}, containerConfig),
            showTextProp && React.createElement(Text, __assign({}, textConfig)),
            React.createElement(Container, __assign({ role: ContainerRoles.Group, "aria-label": DEFAULT_ARIA_LABEL }, linksWrapperConfig), linkColorSelector))) : (React.createElement(Container, { display: ContainerDisplays.Flex, flexAlignItems: ContainerFlexAlignItems.Center, flexGap: ContainerFlexGaps.Size_1 },
            React.createElement(RadioGroup, __assign({}, radioGroupConfig, { "aria-label": DEFAULT_ARIA_LABEL })),
            linkUrlProp && (colorVariantsProp === null || colorVariantsProp === void 0 ? void 0 : colorVariantsProp.length) > trunc && (React.createElement(IconButton, __assign({}, iconButtonConfig))))))));
};
export { CS as ColorSelector };
